import { Injectable } from '@angular/core';
import { Observable, of, ReplaySubject } from 'rxjs';
import { catchError, map, timeout } from 'rxjs/operators';
import { CountryISO } from 'ngx-intl-tel-input';
import latestMeta from 'moment-timezone/data/meta/latest.json';
import { environment } from 'src/environments/environment';
import { ApiRequestService } from './api-request.service';

/**
 * IANA legacy timezone aliases → canonical names.
 * Source: IANA tzdb "backward" file (public domain).
 * Only includes entries whose canonical target exists in moment-timezone's latestMeta.zones.
 * Browsers (especially Chrome) may return legacy names from Intl.DateTimeFormat().
 */
const LEGACY_TIMEZONE_MAP: Record<string, string> = {
  // Africa
  'Africa/Asmera': 'Africa/Asmara',
  'Africa/Timbuktu': 'Africa/Abidjan',
  // Americas
  'America/Argentina/ComodRivadavia': 'America/Argentina/Catamarca',
  'America/Atka': 'America/Adak',
  'America/Buenos_Aires': 'America/Argentina/Buenos_Aires',
  'America/Catamarca': 'America/Argentina/Catamarca',
  'America/Coral_Harbour': 'America/Panama',
  'America/Cordoba': 'America/Argentina/Cordoba',
  'America/Ensenada': 'America/Tijuana',
  'America/Fort_Wayne': 'America/Indiana/Indianapolis',
  'America/Godthab': 'America/Nuuk',
  'America/Indianapolis': 'America/Indiana/Indianapolis',
  'America/Jujuy': 'America/Argentina/Jujuy',
  'America/Knox_IN': 'America/Indiana/Knox',
  'America/Louisville': 'America/Kentucky/Louisville',
  'America/Mendoza': 'America/Argentina/Mendoza',
  'America/Montreal': 'America/Toronto',
  'America/Nipigon': 'America/Toronto',
  'America/Pangnirtung': 'America/Iqaluit',
  'America/Porto_Acre': 'America/Rio_Branco',
  'America/Rainy_River': 'America/Winnipeg',
  'America/Rosario': 'America/Argentina/Cordoba',
  'America/Santa_Isabel': 'America/Tijuana',
  'America/Shiprock': 'America/Denver',
  'America/Thunder_Bay': 'America/Toronto',
  'America/Virgin': 'America/Puerto_Rico',
  'America/Yellowknife': 'America/Edmonton',
  // Antarctica
  'Antarctica/South_Pole': 'Pacific/Auckland',
  // Asia
  'Asia/Ashkhabad': 'Asia/Ashgabat',
  'Asia/Calcutta': 'Asia/Kolkata',
  'Asia/Chongqing': 'Asia/Shanghai',
  'Asia/Chungking': 'Asia/Shanghai',
  'Asia/Dacca': 'Asia/Dhaka',
  'Asia/Harbin': 'Asia/Shanghai',
  'Asia/Istanbul': 'Europe/Istanbul',
  'Asia/Kashgar': 'Asia/Urumqi',
  'Asia/Katmandu': 'Asia/Kathmandu',
  'Asia/Macao': 'Asia/Macau',
  'Asia/Muscat': 'Asia/Dubai',
  'Asia/Rangoon': 'Asia/Yangon',
  'Asia/Saigon': 'Asia/Ho_Chi_Minh',
  'Asia/Tel_Aviv': 'Asia/Jerusalem',
  'Asia/Thimbu': 'Asia/Thimphu',
  'Asia/Ujung_Pandang': 'Asia/Makassar',
  'Asia/Ulan_Bator': 'Asia/Ulaanbaatar',
  // Atlantic
  'Atlantic/Faeroe': 'Atlantic/Faroe',
  'Atlantic/Jan_Mayen': 'Europe/Berlin',
  // Australia
  'Australia/ACT': 'Australia/Sydney',
  'Australia/Canberra': 'Australia/Sydney',
  'Australia/Currie': 'Australia/Hobart',
  'Australia/LHI': 'Australia/Lord_Howe',
  'Australia/North': 'Australia/Darwin',
  'Australia/NSW': 'Australia/Sydney',
  'Australia/Queensland': 'Australia/Brisbane',
  'Australia/South': 'Australia/Adelaide',
  'Australia/Tasmania': 'Australia/Hobart',
  'Australia/Victoria': 'Australia/Melbourne',
  'Australia/West': 'Australia/Perth',
  'Australia/Yancowinna': 'Australia/Broken_Hill',
  // Brazil
  'Brazil/Acre': 'America/Rio_Branco',
  'Brazil/DeNoronha': 'America/Noronha',
  'Brazil/East': 'America/Sao_Paulo',
  'Brazil/West': 'America/Manaus',
  // Canada
  'Canada/Atlantic': 'America/Halifax',
  'Canada/Central': 'America/Winnipeg',
  'Canada/Eastern': 'America/Toronto',
  'Canada/Mountain': 'America/Edmonton',
  'Canada/Newfoundland': 'America/St_Johns',
  'Canada/Pacific': 'America/Vancouver',
  'Canada/Saskatchewan': 'America/Regina',
  'Canada/Yukon': 'America/Whitehorse',
  // Chile
  'Chile/Continental': 'America/Santiago',
  'Chile/EasterIsland': 'Pacific/Easter',
  // Single-word legacy names
  'Cuba': 'America/Havana',
  'Egypt': 'Africa/Cairo',
  'Eire': 'Europe/Dublin',
  'GB': 'Europe/London',
  'GB-Eire': 'Europe/London',
  'Hongkong': 'Asia/Hong_Kong',
  'Iceland': 'Atlantic/Reykjavik',
  'Iran': 'Asia/Tehran',
  'Israel': 'Asia/Jerusalem',
  'Jamaica': 'America/Jamaica',
  'Japan': 'Asia/Tokyo',
  'Kwajalein': 'Pacific/Kwajalein',
  'Libya': 'Africa/Tripoli',
  // Europe
  'Europe/Belfast': 'Europe/London',
  'Europe/Kiev': 'Europe/Kyiv',
  'Europe/Nicosia': 'Asia/Nicosia',
  'Europe/Tiraspol': 'Europe/Chisinau',
  'Europe/Uzhgorod': 'Europe/Kyiv',
  'Europe/Zaporozhye': 'Europe/Kyiv',
  // Mexico
  'Mexico/BajaNorte': 'America/Tijuana',
  'Mexico/BajaSur': 'America/Mazatlan',
  'Mexico/General': 'America/Mexico_City',
  // NZ
  'NZ': 'Pacific/Auckland',
  'NZ-CHAT': 'Pacific/Chatham',
  // Pacific
  'Pacific/Johnston': 'Pacific/Honolulu',
  'Pacific/Ponape': 'Pacific/Pohnpei',
  'Pacific/Samoa': 'Pacific/Pago_Pago',
  'Pacific/Truk': 'Pacific/Chuuk',
  'Pacific/Yap': 'Pacific/Chuuk',
  // Other single-word
  'Poland': 'Europe/Warsaw',
  'Portugal': 'Europe/Lisbon',
  'PRC': 'Asia/Shanghai',
  'ROC': 'Asia/Taipei',
  'ROK': 'Asia/Seoul',
  'Singapore': 'Asia/Singapore',
  'Turkey': 'Europe/Istanbul',
  'W-SU': 'Europe/Moscow',
  // US
  'US/Alaska': 'America/Anchorage',
  'US/Aleutian': 'America/Adak',
  'US/Arizona': 'America/Phoenix',
  'US/Central': 'America/Chicago',
  'US/East-Indiana': 'America/Indiana/Indianapolis',
  'US/Eastern': 'America/New_York',
  'US/Hawaii': 'Pacific/Honolulu',
  'US/Indiana-Starke': 'America/Indiana/Knox',
  'US/Michigan': 'America/Detroit',
  'US/Mountain': 'America/Denver',
  'US/Pacific': 'America/Los_Angeles',
  'US/Samoa': 'Pacific/Pago_Pago',
};

@Injectable({ providedIn: 'root' })
export class GeoLocationService {
  private detectedCountry$ = new ReplaySubject<CountryISO>(1);
  private defaultCountry: CountryISO;

  constructor(private apiRequest: ApiRequestService) {
    // Read from environment, fallback to NZ if not defined
    const envCode = (environment as any).DEFAULT_COUNTRY_CODE?.toLowerCase();
    this.defaultCountry = this.isValidCountryISO(envCode) ? envCode as CountryISO : CountryISO.NewZealand;
    this.detectCountry();
  }

  /** Observable that emits the detected CountryISO exactly once per session. */
  getDetectedCountry(): Observable<CountryISO> {
    return this.detectedCountry$.asObservable();
  }

  private detectCountry(): void {
    this.apiRequest.makeRequestObservable('get', 'v2/geo-ip').pipe(
      timeout(5000),
      map(response => {
        const code = response?.data?.country_code?.toLowerCase();
        return this.isValidCountryISO(code) ? code as CountryISO : null;
      }),
      catchError(() => of(null))
    ).subscribe(country => {
      // Fallback chain: IP -> timezone -> browser language -> environment default
      const resolved = country
        || this.getCountryFromTimezone()
        || this.getCountryFromBrowserLanguage()
        || this.defaultCountry;
      this.detectedCountry$.next(resolved);
    });
  }

  /** Browser language fallback: extracts country from navigator.language (e.g. "en-GB" -> "gb"). */
  private getCountryFromBrowserLanguage(): CountryISO | null {
    try {
      const lang = navigator.language; // e.g. "en-GB", "fr-NZ"
      if (!lang || !lang.includes('-')) return null;

      const parts = lang.split('-');
      const countrySuffix = parts[parts.length - 1].toLowerCase();

      if (countrySuffix.length !== 2) return null;

      return this.isValidCountryISO(countrySuffix) ? countrySuffix as CountryISO : null;
    } catch {
      return null;
    }
  }

  /** Timezone fallback: maps browser timezone to ISO country code via moment-timezone metadata. */
  getCountryFromTimezone(): CountryISO | null {
    try {
      let tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
      if (!tz) return null;

      // Resolve legacy timezone names (e.g. Asia/Calcutta -> Asia/Kolkata)
      tz = LEGACY_TIMEZONE_MAP[tz] || tz;

      const zoneInfo = (latestMeta as any).zones[tz];
      if (!zoneInfo || !zoneInfo.countries || !zoneInfo.countries[0]) {
        return null;
      }

      const lowerCode = zoneInfo.countries[0].toLowerCase();
      return this.isValidCountryISO(lowerCode) ? lowerCode as CountryISO : null;
    } catch {
      return null;
    }
  }

  /** Checks if a lowercase string is a valid CountryISO enum value. */
  private isValidCountryISO(code: string): boolean {
    if (!code) return false;
    return Object.values(CountryISO).includes(code as CountryISO);
  }
}
